import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

const TAG_COLORS = {
  "budgeting":       { bg: "rgba(168,216,185,0.12)", color: "#52D68A" },
  "investing":       { bg: "rgba(212,175,55,0.12)", color: "#D4AF37" },
  "net worth":       { bg: "rgba(138,170,220,0.12)", color: "#8AAADC" },
  "index funds":     { bg: "rgba(212,175,55,0.12)", color: "#D4AF37" },
  "stocks":          { bg: "rgba(212,175,55,0.12)", color: "#D4AF37" },
  "savings":         { bg: "rgba(168,216,185,0.12)", color: "#52D68A" },
  "personal finance":{ bg: "rgba(212,175,55,0.12)", color: "#D4AF37" },
  "fundamentals":    { bg: "rgba(168,216,185,0.12)", color: "#52D68A" },
};

function tagStyle(tag) {
  return TAG_COLORS[tag] || { bg: "rgba(255,255,255,0.06)", color: "var(--text-muted)" };
}

function Tag({ label }) {
  const s = tagStyle(label);
  return (
    <span style={{
      padding: "3px 10px", borderRadius: "20px", fontSize: "11px",
      background: s.bg, color: s.color,
      fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.04em",
    }}>{label}</span>
  );
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function readMins(content) {
  const words = content.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

// ─── Article view ─────────────────────────────────────────────────────────────
function ArticleView({ post, onBack }) {
  return (
    <div className="blog-article-wrap" style={{ maxWidth: "720px", margin: "0 auto", padding: "0 0 80px" }}>
      <button
        onClick={onBack}
        style={{
          background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer",
          fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif", marginBottom: "32px",
          display: "flex", alignItems: "center", gap: "6px", padding: "0",
        }}
      >← Back to Blog</button>

      {/* Tags */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
        {(post.tags || []).map(t => <Tag key={t} label={t} />)}
      </div>

      {/* Title */}
      <h1 style={{
        fontSize: "clamp(28px, 5vw, 44px)", fontWeight: "900",
        color: "var(--text-primary)", letterSpacing: "-0.04em", lineHeight: "1.1",
        marginBottom: "20px", fontFamily: "'Capsule Sans', sans-serif",
      }}>{post.title}</h1>

      {/* Meta */}
      <div style={{
        display: "flex", alignItems: "center", gap: "16px",
        fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace",
        marginBottom: "40px", paddingBottom: "32px", borderBottom: "1px solid var(--border)",
      }}>
        <div style={{
          width: "28px", height: "28px", borderRadius: "50%",
          background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "12px", color: "#121A12", fontWeight: "700",
        }}>{(post.author || "L")[0]}</div>
        <span>{post.author}</span>
        <span>·</span>
        <span>{fmtDate(post.publishedAt)}</span>
        <span>·</span>
        <span>{readMins(post.content)} min read</span>
      </div>

      {/* Content */}
      <style>{`
        .blog-content h2 { font-size: 22px; font-weight: 700; color: #F5F2EE; margin: 32px 0 14px; letter-spacing: -0.025em; }
        .blog-content h3 { font-size: 18px; font-weight: 600; color: #F5F2EE; margin: 24px 0 10px; }
        .blog-content p  { font-size: 16px; color: #b8b4b0; line-height: 1.8; margin-bottom: 18px; }
        .blog-content ul, .blog-content ol { padding-left: 24px; margin-bottom: 18px; }
        .blog-content li { font-size: 16px; color: #b8b4b0; line-height: 1.8; margin-bottom: 6px; }
        .blog-content strong { color: #F5F2EE; font-weight: 600; }
        .blog-content a { color: #D4AF37; text-decoration: none; border-bottom: 1px solid rgba(212,175,55,0.3); }
        .blog-content blockquote { border-left: 3px solid #D4AF37; padding-left: 20px; margin: 24px 0; color: #aaa; font-style: italic; }
        .blog-content code { background: #121C12; padding: 2px 8px; border-radius: 5px; font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #D4AF37; }
        .blog-content pre  { background: #0D1410; border: 1px solid #1E2C1E; border-radius: 10px; padding: 20px; overflow-x: auto; margin-bottom: 18px; }
        .blog-content pre code { background: none; padding: 0; }
        @media (max-width: 768px) {
          .blog-posts-grid { grid-template-columns: 1fr !important; }
          .blog-page-wrap { padding: 20px 16px !important; }
          .blog-article-wrap { padding: 20px 16px 60px !important; }
        }
      `}</style>
      <div
        className="blog-content"
        style={{ fontFamily: "'Capsule Sans', sans-serif" }}
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
    </div>
  );
}

// ─── Blog card ────────────────────────────────────────────────────────────────
function PostCard({ post, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "var(--bg-hover)" : "var(--bg-main)",
        border: `1px solid ${hovered ? "#243424" : "var(--border)"}`,
        borderRadius: "16px", padding: "28px", cursor: "pointer",
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-2px)" : "none",
      }}
    >
      {/* Tags */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
        {(post.tags || []).map(t => <Tag key={t} label={t} />)}
      </div>

      {/* Title */}
      <h2 style={{
        fontSize: "20px", fontWeight: "700", color: "var(--text-primary)",
        letterSpacing: "-0.025em", lineHeight: "1.3", marginBottom: "12px",
        fontFamily: "'Capsule Sans', sans-serif",
      }}>{post.title}</h2>

      {/* Excerpt */}
      <p style={{
        fontSize: "14px", color: "var(--text-muted)", lineHeight: "1.6", marginBottom: "20px",
      }}>{post.excerpt}</p>

      {/* Meta */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace",
        borderTop: "1px solid #121C12", paddingTop: "16px",
      }}>
        <span>{post.author} · {fmtDate(post.publishedAt)}</span>
        <span style={{ color: hovered ? "#D4AF37" : "#555", transition: "color 0.2s" }}>
          {readMins(post.content)} min read →
        </span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function BlogPage() {
  const [posts,      setPosts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activePost, setActivePost] = useState(null);
  const [activeTag,  setActiveTag]  = useState("all");

  useEffect(() => {
    fetch(`${API}/api/v1/blog`)
      .then(r => r.json())
      .then(data => { setPosts(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Collect all tags
  const allTags = ["all", ...new Set(posts.flatMap(p => p.tags || []))];
  const filtered = activeTag === "all" ? posts : posts.filter(p => (p.tags || []).includes(activeTag));

  if (activePost) return (
    <div className="blog-page-wrap" style={{ padding: "40px", maxWidth: "1100px", fontFamily: "'Capsule Sans', sans-serif" }}>
      <ArticleView post={activePost} onBack={() => setActivePost(null)} />
    </div>
  );

  return (
    <div className="blog-page-wrap" style={{ padding: "32px 40px", maxWidth: "1100px", fontFamily: "'Capsule Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: "12px" }}>LEDGER BLOG</div>
        <h1 style={{ fontSize: "36px", fontWeight: "900", color: "var(--text-primary)", letterSpacing: "-0.04em", marginBottom: "10px" }}>
          Financial Insights
        </h1>
        <p style={{ fontSize: "15px", color: "var(--text-muted)", maxWidth: "480px" }}>
          Practical guides and ideas to help you build wealth, cut debt, and make smarter money decisions.
        </p>
      </div>

      {/* Tag filter */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "32px" }}>
        {allTags.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveTag(tag)}
            style={{
              padding: "7px 16px", borderRadius: "20px", cursor: "pointer",
              fontSize: "12px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "500",
              background: activeTag === tag ? "rgba(212,175,55,0.15)" : "var(--bg-card)",
              color: activeTag === tag ? "#D4AF37" : "#777",
              border: activeTag === tag ? "1px solid rgba(212,175,55,0.3)" : "1px solid var(--border-strong)",
              transition: "all 0.15s",
            }}
          >
            {tag === "all" ? "All Posts" : tag}
          </button>
        ))}
      </div>

      {/* Posts grid */}
      {loading ? (
        <div style={{ color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", padding: "60px 0", textAlign: "center" }}>
          Loading posts…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", padding: "60px 0", textAlign: "center" }}>
          No posts yet. Check back soon.
        </div>
      ) : (
        <div className="blog-posts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "20px" }}>
          {filtered.map(post => (
            <PostCard key={post.id} post={post} onClick={() => setActivePost(post)} />
          ))}
        </div>
      )}
    </div>
  );
}
